// CSV / XLSX export (spec §9.4). CSV uses a plain Blob so it works with zero
// dependencies; XLSX uses SheetJS, bundled locally (not loaded from a CDN) so
// export works offline like everything else in the app. SheetJS (~430KB) is loaded
// lazily on first XLSX export rather than bundled into the app shell — CSV export
// and every other screen work without ever fetching it.

import { slug } from './format';
import { state, categoryName } from '../state';

function rowsForExpenses(): Array<[string, number, string, string, string]> {
  return [...state.expenses]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((e) => [e.date, e.amount, e.currency, categoryName(e.categoryId), e.explanation || '']);
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function csvEscape(v: string | number): string {
  const s = String(v);
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

export function exportCSV(): void {
  const rows: Array<(string | number)[]> = [
    ['Date', 'Amount', 'Currency', 'Category', 'Explanation'],
    ...rowsForExpenses(),
  ];
  const csv = rows.map((r) => r.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${slug(state.trip.name)}-expenses.csv`);
}

export async function exportXLSX(): Promise<void> {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();

  const expRows = [['Date', 'Amount', 'Currency', 'Category', 'Explanation'], ...rowsForExpenses()];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(expRows), 'Expenses');

  const catTotals: Record<string, number> = {};
  state.expenses.forEach((e) => {
    const key = categoryName(e.categoryId) + ' / ' + e.currency;
    catTotals[key] = (catTotals[key] || 0) + e.amount;
  });
  const catRows = [
    ['Category', 'Currency', 'Total'],
    ...Object.entries(catTotals).map(([k, v]) => {
      const [cat, cur] = k.split(' / ');
      return [cat, cur, v];
    }),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(catRows), 'Summary by Category');

  const curTotals: Record<string, number> = {};
  const curCounts: Record<string, number> = {};
  state.expenses.forEach((e) => {
    curTotals[e.currency] = (curTotals[e.currency] || 0) + e.amount;
    curCounts[e.currency] = (curCounts[e.currency] || 0) + 1;
  });
  const curRows = [
    ['Currency', 'Total', 'Transactions'],
    ...Object.entries(curTotals).map(([c, v]) => [c, v, curCounts[c]]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(curRows), 'Summary by Currency');

  const t = state.trip;
  const tripRows = [
    ['Field', 'Value'],
    ['Trip name', t.name],
    ['Description', t.description],
    ['Countries', t.countries.join(', ')],
    ['Budget', t.totalBudget],
    ['Base currency', t.baseCurrency],
    ['Start date', t.startDate],
    ['End date', t.endDate],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(tripRows), 'Trip Info');

  XLSX.writeFile(wb, `${slug(state.trip.name)}-export.xlsx`);
}
